import React, { Suspense } from 'react'
import './lib/initialNavigation' // evaluated at page load: ChatPage (lazy) reads it for the refresh-recovery toast
import * as Sentry from "@sentry/react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { BrandingProvider, useBranding } from './contexts/BrandingContext'
import { OnboardingProvider, useOnboarding } from './contexts/OnboardingContext'
import { TourProvider } from './components/onboarding/Tour/TourProvider'
// #3962: route-level code splitting. Only the guest entry path stays in the
// entry chunk (HomePageV2, the three /discover pages, LoginPage, VerifyPage:
// a lazy first page would add a second round trip on a phone); every other
// page is its own chunk, loaded when its route first renders. One <Suspense>
// in AppRoutes covers them all. lazyWithReload (lib/lazyWithReload.js) turns a
// chunk that vanished in a deploy into ONE guarded reload instead of the error
// boundary. The local identifier is unchanged for each page, so the <Route>
// elements below read exactly as before.
import HomePageV2 from './pages/HomePageV2'
import GuestDiscoveryContextsPage from './pages/discovery/GuestDiscoveryContextsPage' // #1921 J2
import GuestDiscoveryShortlistPage from './pages/discovery/GuestDiscoveryShortlistPage' // #1921 J3
import GuestChooserPage from './pages/discovery/GuestChooserPage' // #1922 J2 "help me choose" chooser
import LoginPage from './pages/LoginPage'
import VerifyPage from './pages/VerifyPage'
import { lazyWithReload } from './lib/lazyWithReload'
// PersonaManagementPage (HIDDEN FOR LAUNCH, Issue #43) and HelpCenterPage
// (HIDDEN FOR LAUNCH) had unused static imports here; removed in #3962 so the
// dead code does not ride in the entry chunk. Re-add them as lazyWithReload
// when their routes are un-hidden.
// PathwaysPage deprecated - functionality moved to ScenarioAllocationPage pathways tab
// DemoPage deprecated - demo flow handled directly on HomePage (Issue #282)
import ProtectedRoute from './components/ProtectedRoute'
import { resolveLearnerHome, DASHBOARD_HOME } from './lib/navigation/resolveLearnerHome' // #1521
import { HelpPanelProvider, useHelpPanel } from './contexts/HelpPanelContext'
import { ToastProvider, useToast } from './contexts/ToastContext'
import { useTranslation } from 'react-i18next'
import TestingPanelHost from './components/testing/TestingPanelHost'
import BugReporter from './components/bugReporter/BugReporter'
import HomeButton from './components/homeButton/HomeButton'
import AnalyticsAskFab from './components/analyticsAsk/AnalyticsAskFab'
import { isPublicRoute } from './lib/publicRoutes'
import { FabStackProvider, useFabStackSlot } from './contexts/FabStackContext'
import { FAB_PRIORITY } from './lib/fabStack'
import { isFabAllowedOnMobile, FAB_MOBILE_BREAKPOINT_QUERY } from './lib/fabMobileVisibility'
import FabMenu from './components/fabMenu/FabMenu'
import './App.css'

// Lazy route pages (#3962). The key names the chunk for the reload guard.
const HomePage = lazyWithReload(() => import('./pages/HomePage'), 'HomePage')
const YomaSharedSpacePage = lazyWithReload(() => import('./pages/YomaSharedSpacePage'), 'YomaSharedSpacePage')
const TermsPage = lazyWithReload(() => import('./pages/TermsPage'), 'TermsPage')
const PrivacyPage = lazyWithReload(() => import('./pages/PrivacyPage'), 'PrivacyPage')
const UnsubscribePage = lazyWithReload(() => import('./pages/UnsubscribePage'), 'UnsubscribePage')
const DashboardPage = lazyWithReload(() => import('./pages/DashboardPage'), 'DashboardPage') // Legacy - preserved at /dashboard-legacy
const DashboardPageV2 = lazyWithReload(() => import('./pages/DashboardPageV2'), 'DashboardPageV2') // New main dashboard
const MessengerPage = lazyWithReload(() => import('./pages/MessengerPage'), 'MessengerPage') // #1521 messenger learner home (flag-gated)
const MyAssignmentsPage = lazyWithReload(() => import('./pages/MyAssignmentsPage'), 'MyAssignmentsPage')
const ScenariosPageWorking = lazyWithReload(() => import('./pages/ScenariosPageWorking'), 'ScenariosPageWorking')
const ScenarioAuthoringPage = lazyWithReload(() => import('./pages/ScenarioAuthoringPage'), 'ScenarioAuthoringPage')
const ReviewDashboardPage = lazyWithReload(() => import('./pages/ReviewDashboardPage'), 'ReviewDashboardPage')
const FacilitatorInboxPage = lazyWithReload(() => import('./pages/FacilitatorInboxPage'), 'FacilitatorInboxPage')
const ScenarioDetailPage = lazyWithReload(() => import('./pages/ScenarioDetailPage'), 'ScenarioDetailPage')
const ScenarioDashboard = lazyWithReload(() => import('./pages/ScenarioDashboard'), 'ScenarioDashboard')
// ChatPage is the biggest chunk and the guest path's last hop
// (/discover -> commitment sheet -> /chat/:runId?demo=true), so
// GuestChatPrefetch warms it while the guest is still on /discover.
const loadChatPage = () => import('./pages/ChatPage')
const ChatPage = lazyWithReload(loadChatPage, 'ChatPage')
const MyChatSessionsPage = lazyWithReload(() => import('./pages/MyChatSessionsPage'), 'MyChatSessionsPage')
const MyPerformancePage = lazyWithReload(() => import('./pages/MyPerformancePage'), 'MyPerformancePage')
const LearnerProgressPage = lazyWithReload(() => import('./pages/LearnerProgressPage'), 'LearnerProgressPage')
const InsightsPage = lazyWithReload(() => import('./pages/InsightsPage'), 'InsightsPage')
const LearnerDetailPage = lazyWithReload(() => import('./pages/LearnerDetailPage'), 'LearnerDetailPage')
const CohortReportViewPage = lazyWithReload(() => import('./pages/CohortReportViewPage'), 'CohortReportViewPage')
const ScenarioAllocationPage = lazyWithReload(() => import('./pages/ScenarioAllocationPage'), 'ScenarioAllocationPage')
const CommunityPage = lazyWithReload(() => import('./pages/CommunityPage'), 'CommunityPage')
const MyScenariosPage = lazyWithReload(() => import('./pages/MyScenariosPage'), 'MyScenariosPage')
const ApiKeysPage = lazyWithReload(() => import('./pages/ApiKeysPage'), 'ApiKeysPage')
const ProgressStoriesAdminPage = lazyWithReload(() => import('./pages/admin/ProgressStoriesAdminPage'), 'ProgressStoriesAdminPage')
const AnalyticsEvalPage = lazyWithReload(() => import('./pages/admin/AnalyticsEvalPage'), 'AnalyticsEvalPage')
const WeeklySignalAdminPage = lazyWithReload(() => import('./pages/admin/WeeklySignalAdminPage'), 'WeeklySignalAdminPage')
const VoiceCicDashboardPage = lazyWithReload(() => import('./pages/admin/VoiceCicDashboardPage'), 'VoiceCicDashboardPage')
const AiAssistAdminPage = lazyWithReload(() => import('./pages/admin/AiAssistAdminPage'), 'AiAssistAdminPage')
const AuthoringInsightsAdminPage = lazyWithReload(() => import('./pages/admin/AuthoringInsightsAdminPage'), 'AuthoringInsightsAdminPage')
const VoiceHealthDashboardPage = lazyWithReload(() => import('./pages/admin/VoiceHealthDashboardPage'), 'VoiceHealthDashboardPage')
const AdminCrisisEscalationPage = lazyWithReload(() => import('./pages/admin/AdminCrisisEscalationPage'), 'AdminCrisisEscalationPage')
const AdminCrisisListPage = lazyWithReload(() => import('./pages/admin/AdminCrisisListPage'), 'AdminCrisisListPage')
const OrgDashboardPage = lazyWithReload(() => import('./pages/OrgDashboardPage'), 'OrgDashboardPage')
// NOT lazyWithReload: Help / Scenario-info is an optional side panel opened
// mid-chat, so a failed fetch must never reload the page (that would end a chat
// or voice session) nor reach the root error boundary. See HelpPanelHost.
//
// ONE memoised import promise per document (#4382), the same rule as
// lazyWithReload's importOnce: in a build, Vite's preload helper marks each CSS
// dependency as seen before its <link> loads and never forgets a failure, so a
// second import() in this document can resolve the JS without its stylesheet
// (an unstyled panel, no error). A failed load therefore stays failed until the
// learner reloads the page; HelpPanelHost says so instead of retrying.
let helpPanelImport = null
const loadHelpPanel = () => {
  if (!helpPanelImport) helpPanelImport = import('./components/HelpPanel')
  return helpPanelImport
}
// Issue #818 (refresh 2026-05-14): learner self-service settings page.
const SettingsPage = lazyWithReload(() => import('./pages/SettingsPage'), 'SettingsPage')

// #1018: redirect /reports/:id → /learner-progress/reports/:id (bookmark compat).
function ReportViewRedirect() {
  const { id } = useParams();
  return <Navigate to={`/learner-progress/reports/${id}`} replace />;
}

// #1377 P3: redirect old /scenario-allocation → /content, preserving the
// query string so every existing deep link (?view=pathways, ?view=reports,
// ?view=assignments&learner=..., etc.) still lands on the same section.
function ScenarioAllocationRedirect() {
  const location = useLocation();
  return <Navigate to={`/content${location.search}`} replace />;
}

// Redirect old /help/* routes to slide-over panel on dashboard
function HelpRedirect() {
  const params = useParams();
  const slug = params['*'] || '';
  return <Navigate to={`/dashboard?help=${slug}`} replace />;
}

// /scenarios/:id/login — redirect to login, then back to scenario after auth (#469).
// #1411: guests are technically `isAuthenticated`, so checking that alone bounced
// guests right back to /scenarios/:id and broke the facilitator-share + org-private
// deep-link flows. Treat guests as needing real login.
function ScenarioLoginRedirect() {
  const { id } = useParams();
  const { isAuthenticated, user } = useAuth();
  // Already logged in as a real user → skip login, go straight to scenario.
  if (isAuthenticated && !user?.is_guest) {
    return <Navigate to={`/scenarios/${id}`} replace />;
  }
  // Signed-out OR guest → real login (preserve scenario as post-login redirect).
  return <Navigate to="/login" state={{ from: { pathname: `/scenarios/${id}` } }} replace />;
}

// #1411: wrap ScenarioDetailPage with key={id} so direct navigation between
// scenarios (/scenarios/1 → /scenarios/2) forces a fresh mount. Without this,
// the in-flight-fetch ref guard inside the page would suppress the second load.
function ScenarioDetailPageKeyed() {
  const { id } = useParams();
  return <ScenarioDetailPage key={id} />;
}

// #2915 / D-144 option (a): facilitators, org_admins and admins land on the
// main menu (/dashboard) and reach the org dashboard through its card. The
// Mega25 redirect into /org-dashboard#highlights (and its fromOrgDashboard
// escape hatch for the circular redirect) is gone, so there is nothing left to
// bounce between. Staff go to /dashboard explicitly rather than through
// resolveLearnerHome: a messenger_is_home org must not send staff to /messenger.
function isStaffRole(user) {
  return (
    user?.role === 'facilitator' ||
    user?.role === 'org_admin' ||
    user?.role === 'admin'
  );
}

// Shared by the auth-loading gate and the lazy-route Suspense fallback (#3962).
function AppLoading() {
  return (
    <div className="app-loading">
      <div className="loading-spinner" />
      <p>Loading application...</p>
    </div>
  );
}

// #3962: while a guest sits on /discover*, fetch the ChatPage chunk in idle
// time so the commitment-sheet -> /chat/:runId?demo=true hop finds it warm.
// A prefetch failure is silent here and is NOT retried: the route reuses the
// failed promise (ChatPage.prefetch, lazyWithReload.js), so the chat
// navigation takes the guarded reload. A retried import() would resolve
// without the chunk's CSS (Vite's preload cache). There is deliberately no global
// `vite:preloadError` reload listener (see lazyWithReload.js): it would reload
// the page, discarding the guest's open dialog, for this speculative fetch.
function GuestChatPrefetch() {
  const { pathname } = useLocation();
  const onDiscover = pathname === '/discover' || pathname.startsWith('/discover/');
  React.useEffect(() => {
    if (!onDiscover) return undefined;
    const run = () => { ChatPage.prefetch(); };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(run);
      return () => window.cancelIdleCallback?.(id);
    }
    const id = setTimeout(run, 1500);
    return () => clearTimeout(id);
  }, [onDiscover]);
  return null;
}

function AppRoutes() {
  const { isAuthenticated, isLoading, user } = useAuth();
  // #1521: learner home is flag-resolved (/messenger for messenger-enabled
  // orgs, /dashboard otherwise). Raw wire branding — the pure resolver
  // accepts the snake_case shape, so this needs no BrandingContext timing.
  const postLoginPath = isStaffRole(user)
    ? DASHBOARD_HOME
    : resolveLearnerHome(user?.branding);

  if (isLoading) {
    return <AppLoading />;
  }

  return (
    // #3962: ONE boundary around the route table, inside the providers, so a
    // lazy page suspends here (context stays mounted, the FABs outside
    // AppContent do not flash) rather than unmounting the tree at the root.
    <Suspense fallback={<AppLoading />}>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        // Guest users should see login page to upgrade their account
        // Only redirect fully authenticated (non-guest) users to dashboard
        (isAuthenticated && !user?.is_guest) ? <Navigate to={postLoginPath} replace /> : <LoginPage />
      } />
      <Route path="/auth/verify" element={<VerifyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      {/* Public digest unsubscribe landing — no auth, token-gated (Issue #870) */}
      <Route path="/unsubscribe/digest/:token" element={<UnsubscribePage />} />
      {/* #3258: where the pre-init scrub lands a real load (token moved to memory) */}
      <Route path="/unsubscribe/digest" element={<UnsubscribePage />} />
      {/* /demo deprecated - redirect to homepage */}
      <Route path="/demo" element={<Navigate to="/" replace />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPageV2 />
        </ProtectedRoute>
      } />
      {/* #1521 messenger learner home — always routable; whether it is the
          DEFAULT home is decided by resolveLearnerHome (branding flag). The
          page itself renders a redirect-to-dashboard fallback for flag-OFF
          orgs so a stale deep-link can't strand anyone. */}
      <Route path="/messenger" element={
        <ProtectedRoute>
          <MessengerPage />
        </ProtectedRoute>
      } />
      {/* Legacy dashboard preserved for reference - can be removed later */}
      <Route path="/dashboard-legacy" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/my-assignments" element={
        <ProtectedRoute>
          <MyAssignmentsPage />
        </ProtectedRoute>
      } />
      <Route path="/scenarios" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <ScenariosPageWorking />
        </ProtectedRoute>
      } />
      <Route path="/scenarios/author" element={
        <ProtectedRoute allowedRoles={['learner', 'facilitator', 'org_admin', 'admin']}>
          <ScenarioAuthoringPage />
        </ProtectedRoute>
      } />
      {/* /scenarios/:id/login — facilitator share link: sign in first, then auto-start (#469) */}
      <Route path="/scenarios/:id/login" element={<ScenarioLoginRedirect />} />
      {/* /scenarios/:id — guest deep link: auto-guest, auto-start (#469).
          #1411: keyed wrapper forces remount on id change. */}
      <Route path="/scenarios/:id" element={<ScenarioDetailPageKeyed />} />
      {/* Issue #167 - Scenario Dashboard (learning hub with objectives and focus sessions) */}
      <Route path="/scenarios/:id/dashboard" element={
        <ProtectedRoute>
          <ScenarioDashboard />
        </ProtectedRoute>
      } />
      <Route path="/workflows/review" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <ReviewDashboardPage />
        </ProtectedRoute>
      } />
      {/* #199 — Facilitator Inbox MVP slice: aggregates pending facilitator
          tasks (today: pending publish reviews, reusing the same
          VersionWorkflowAPI calls /workflows/review already makes). Same
          role gate as /workflows/review — a learner hitting this route
          sees ProtectedRoute's access-denied wall, not a 404 or redirect. */}
      <Route path="/inbox" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <FacilitatorInboxPage />
        </ProtectedRoute>
      } />
      <Route path="/chat-sessions" element={
        <ProtectedRoute>
          <MyChatSessionsPage />
        </ProtectedRoute>
      } />
      <Route path="/chat/:runId" element={
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      } />
      <Route path="/my-performance" element={
        <ProtectedRoute>
          <MyPerformancePage />
        </ProtectedRoute>
      } />
      <Route path="/learner-progress" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <LearnerProgressPage />
        </ProtectedRoute>
      } />
      {/* Surface-C analytics assistant — full-page home (same panel as the FAB drawer). Plan 6. */}
      <Route path="/insights" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <InsightsPage />
        </ProtectedRoute>
      } />
      {/* #1018: nested report view under /learner-progress (same RBAC as parent) */}
      <Route path="/learner-progress/reports/:id" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <CohortReportViewPage />
        </ProtectedRoute>
      } />
      {/* #1018: /learner-progress/reports → redirect to Reports tab (?view=reports) */}
      <Route path="/learner-progress/reports" element={
        <Navigate to="/learner-progress?view=reports" replace />
      } />
      <Route path="/learner-progress/:userId" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <LearnerDetailPage />
        </ProtectedRoute>
      } />
      {/* Issue #825 Phase 2B — Cohort reports (standalone routes kept as redirects for bookmark compatibility — #1018) */}
      <Route path="/reports" element={
        <Navigate to="/learner-progress?view=reports" replace />
      } />
      <Route path="/reports/:id" element={<ReportViewRedirect />} />
{/* HIDDEN FOR LAUNCH - Persona Management route
      Before un-hiding: PersonasService.updatePersona/deletePersona enforce
      strict creator-only ownership (`backend/src/services/personas.js:137,203`).
      The route layer permits org_admin/facilitator/admin, but the service will
      403 if the caller didn't create the persona. Add a role bypass mirroring
      ScenariosService.archiveScenario before exposing this UI.
      <Route path="/personas" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <PersonaManagementPage />
        </ProtectedRoute>
      } />
*/}
      {/* #1377 P3: "Content" hub — promoted from /scenario-allocation (same
          component/tabs: groups/pathways/scenarios/assignments/reports).
          /scenario-allocation kept as an alias redirect below so every
          existing bookmark/deep-link keeps working. */}
      <Route path="/content" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <ScenarioAllocationPage />
        </ProtectedRoute>
      } />
      <Route path="/scenario-allocation" element={<ScenarioAllocationRedirect />} />
      {/* Issue #339 - Pathways (redirect to Content hub pathways tab) */}
      <Route path="/pathways" element={<Navigate to="/content?view=pathways" replace />} />
      <Route path="/community" element={
        <ProtectedRoute>
          <CommunityPage />
        </ProtectedRoute>
      } />
      <Route path="/my-scenarios" element={
        <ProtectedRoute allowedRoles={['learner']}>
          <MyScenariosPage />
        </ProtectedRoute>
      } />
      <Route path="/org-dashboard" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <OrgDashboardPage />
        </ProtectedRoute>
      } />
      {/* Issue #307 - API Keys for external integrations (admin only) */}
      <Route path="/admin/api-keys" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ApiKeysPage />
        </ProtectedRoute>
      } />
      {/* Surface-C analytics eval review — admin-only flagging tool (org-scoped). Plan 6. */}
      <Route path="/admin/analytics-eval" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AnalyticsEvalPage />
        </ProtectedRoute>
      } />
      {/* Issue #1113 - Progress stories admin (facilitator-curated weekly highlights) */}
      <Route path="/admin/progress-stories" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <ProgressStoriesAdminPage />
        </ProtectedRoute>
      } />
      {/* Issue #1111 - Weekly signal admin (worklist hero Row 3 curation) */}
      <Route path="/admin/weekly-signal" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <WeeklySignalAdminPage />
        </ProtectedRoute>
      } />
      {/* Track A T1d (plan §9) - Voice CIC observability dashboard */}
      <Route path="/admin/voice-cic" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <VoiceCicDashboardPage />
        </ProtectedRoute>
      } />
      {/* Issue #1123 + #1125 AC5 - AI-assist repeat-flag aggregation + FP surface */}
      <Route path="/admin/ai-assist" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <AiAssistAdminPage />
        </ProtectedRoute>
      } />
      {/* Issue #2535 - Scenario Authoring Insights (reader for the
          authoring_sessions telemetry stream; the endpoints are gated
          requireAdmin — platform-admin only, not requireFacilitatorOrAdmin —
          so this route must stay admin-only too). */}
      <Route path="/admin/authoring-insights" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AuthoringInsightsAdminPage />
        </ProtectedRoute>
      } />
      {/* Issue #1362 Phase 2 - Voice Health Dashboard (in-app, org-scoped) */}
      <Route path="/admin/voice-health" element={
        <ProtectedRoute allowedRoles={['facilitator', 'org_admin', 'admin']}>
          <VoiceHealthDashboardPage />
        </ProtectedRoute>
      } />
      {/* Issue #1670 Phase 3 - Crisis escalation admin (platform-admin only,
          deliberately cross-org — the safety-owner transcript/resolution
          view). /admin/crisis/:id is baked into every crisis email's
          transcriptUrl (CrisisEscalationService) — do not rename. */}
      <Route path="/admin/crisis" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminCrisisListPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/crisis/:id" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminCrisisEscalationPage />
        </ProtectedRoute>
      } />

      {/* Issue #818 (refresh 2026-05-14) - Learner self-service settings */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />

      {/* Issue #370 - In-app help center (redirects to slide-over panel) */}
      <Route path="/help/*" element={<HelpRedirect />} />

      {/* Issue #1787 - Tacit x Yoma shared build-space. Static route, NOT an
          org-branded audience page (no Yoma organization row) — must be
          declared before /for/:audience so it isn't shadowed. */}
      <Route path="/for/yoma" element={<YomaSharedSpacePage />} />

      {/* Issue #393 - Generic audience landing pages */}
      <Route path="/for/:audience" element={<HomePageV2 />} />
      {/* Backward compat: /unicef -> /for/unicef */}
      <Route path="/unicef" element={<Navigate to="/for/unicef" replace />} />

      {/* Home route - redirect based on auth status (guests see homepage).
          #2915: facilitator/org_admin/admin land on /dashboard (the main menu). */}
      <Route path="/" element={
        isAuthenticated && !user?.is_guest ? <Navigate to={postLoginPath} replace /> : <HomePageV2 />
      } />
      {/* #3756 / ADR-0041: identical to `/`. When the server serves the static
          marketing landing at `/` (SITE_MODE preview|live), a full page load
          of `/` no longer reaches this SPA, so the landing's /site/session.js
          forwards a stored non-guest session here to keep the role-based
          post-login landing. In-app navigate('/') still renders the route
          above client-side. */}
      <Route path="/home" element={
        isAuthenticated && !user?.is_guest ? <Navigate to={postLoginPath} replace /> : <HomePageV2 />
      } />

      {/* Guest discovery — J1's "Find my conversation" primary CTA (#1921).
          Public, no auth: same guest-catalogue posture as / itself. */}
      <Route path="/discover" element={<GuestDiscoveryContextsPage />} />
      <Route path="/discover/choose" element={<GuestChooserPage />} />
      <Route path="/discover/:contextSlug" element={<GuestDiscoveryShortlistPage />} />

      {/* V1 Landing Page - legacy */}
      <Route path="/v1" element={<HomePage />} />

      {/* Catch-all route */}
      <Route path="*" element={
        <div className="not-found">
          <h2>Page Not Found</h2>
          <p>The page you're looking for doesn't exist.</p>
          <button onClick={() => window.history.back()}>Go Back</button>
        </div>
      } />
    </Routes>
    </Suspense>
  );
}

// SEO: Update <link rel="canonical"> on route changes
// Public routes get their own canonical; all others fall back to homepage
const CANONICAL_ROUTES = ['/', '/login', '/terms', '/privacy', '/for/digcomp', '/for/yoma'];

function CanonicalTag() {
  const location = useLocation();
  React.useEffect(() => {
    const link = document.querySelector('link[rel="canonical"]');
    if (!link) return;
    const path = location.pathname.replace(/\/+$/, '') || '/';
    const href = CANONICAL_ROUTES.includes(path)
      ? `https://tacit.coach${path === '/' ? '/' : path}`
      : 'https://tacit.coach/';
    link.setAttribute('href', href);
  }, [location.pathname]);
  return null;
}

function ErrorFallback() {
  return (
    <div className="error-fallback">
      <h2>Something went wrong</h2>
      <p>We've been notified and are looking into it.</p>
      <button onClick={() => window.location.reload()}>Reload Page</button>
    </div>
  );
}

const HELP_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/**
 * FloatingHelpButton — registers 'help' into the shared 'menu'-corner
 * FabStack (#1696, 2026-07-27 DECIDED). Renders nothing itself: FabMenu
 * (components/fabMenu/FabMenu.jsx) owns the actual button and calls back
 * into `openHelp` on tap.
 *
 * #1696 also drops the old CHAT_PHASE.COMPLETE-based bottom-right relocation
 * (the `body:has(.coaching-mode) .floating-help-button` App.css rule this
 * component used to dodge the debrief coach FAB with): now that Help lives
 * inside a closed-by-default menu rather than as an always-visible FAB, it
 * never visually competes with the coach FAB in the first place, so the
 * chat-phase subscription that existed purely to suppress that collision is
 * no longer needed here.
 */
function FloatingHelpButton() {
  const { isAuthenticated, user } = useAuth();
  const { isOpen, openHelp } = useHelpPanel();
  const location = useLocation();

  // Shared public-route definition (frontend/src/lib/publicRoutes.js) — also used by the analytics FAB.
  const isPublic = isPublicRoute(location.pathname);
  const isGuest = user?.is_guest;

  // #2079: mobile viewport tracking, mirroring VoiceChat.jsx's existing
  // `matchMedia('(max-width: 767px)')` pattern (same breakpoint via the
  // shared FAB_MOBILE_BREAKPOINT_QUERY constant so it can't drift from the
  // CSS tokens' <=767px block in App.css). Eager init avoids a
  // desktop-to-mobile flash on first paint (safe in this Vite SPA, no SSR).
  // Guarded (typeof window / matchMedia) for any no-jsdom render of this
  // component (project convention — see VoiceCaptureIndicator.jsx /
  // ThemeContext.jsx for the same guard; sibling fix in AnalyticsAskFab.jsx).
  const [isMobile, setIsMobile] = React.useState(() => (
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(FAB_MOBILE_BREAKPOINT_QUERY).matches
      : false
  ));
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(FAB_MOBILE_BREAKPOINT_QUERY);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  // Hide on mobile everywhere except /dashboard and /org-dashboard
  // (mega77 human-test, staging 00907-6p5 session 138). Desktop unaffected —
  // see lib/fabMobileVisibility.js's docstring for why this is a visibility
  // gate here rather than a fabStack.js stacking gate (which would also
  // suppress the unrelated CoachBubble debrief FAB).
  const mobileAllowed = isFabAllowedOnMobile({ isMobile, pathname: location.pathname });

  const visible = isAuthenticated && !isGuest && !isPublic && !isOpen && mobileAllowed;

  useFabStackSlot('menu', 'help', FAB_PRIORITY.HELP, visible, {
    icon: HELP_ICON,
    label: 'Help',
    ariaLabel: 'Open help',
    onClick: () => openHelp(),
    testId: 'fab-menu-item-help',
  });

  return null;
}

// #3962: HelpPanel renders null while closed, but statically it dragged the help
// guides, the syntax highlighter and the PDF exporter (~1.2 MB unpacked) into
// the entry chunk, so its chunk is fetched on first open. It is OPTIONAL UI the
// learner opens in the middle of a chat or voice session, so unlike a route page
// a failed fetch (flaky mobile network, or a chunk renamed by a deploy) must not
// reload the page and must not reach the root error boundary. The import is
// caught here: the panel hides (keeping the context it was opened with) and a
// toast says Help did not load, with a Reload action. The learner chooses
// whether to end their session; the code never reloads by itself. The load is
// NOT retried in this document (see loadHelpPanel), so a later open shows the
// same toast again rather than an unstyled panel. The message does not say
// "check your connection": after a deploy the server answered fine.
const HELP_LOAD_TOAST_MS = 20000
function HelpPanelHost() {
  const { isOpen, dismissHelp } = useHelpPanel();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const [Panel, setPanel] = React.useState(null);
  React.useEffect(() => {
    if (!isOpen || Panel) return undefined;
    let cancelled = false;
    loadHelpPanel().then(
      (mod) => { if (!cancelled) setPanel(() => mod.default); },
      () => {
        if (cancelled) return;
        dismissHelp();
        showToast(
          t('errors.helpLoadFailed', "Help couldn't load. Reload the page to open it."),
          'error',
          HELP_LOAD_TOAST_MS,
          { label: t('errors.reload', 'Reload'), onClick: () => window.location.reload() },
        );
      },
    );
    return () => { cancelled = true; };
  }, [isOpen, Panel, dismissHelp, showToast, t]);
  return isOpen && Panel ? <Panel /> : null;
}

function AppContent() {
  const { isOpen, panelWidth } = useHelpPanel();

  // #1569: publish the help-panel width on the document ROOT, not on .App.
  // --right-panel-offset is declared on :root (App.css) as
  //   calc(var(--help-panel-width) + var(--testing-panel-width) + var(--bug-reporter-width))
  // and is substituted in :root's context. When --help-panel-width was set on
  // .App (a child of :root, via inline style) the root-level calc resolved it
  // to its 0px fallback, so `.App { margin-right: var(--right-panel-offset) }`
  // never reserved room for the help panel — it overlaid the chat and hid
  // "End Scenario". Mirror TestingPanel (--testing-panel-width) and BugReporter
  // (--bug-reporter-width), which publish on document.documentElement and whose
  // squeeze works correctly. Verified: var on .App → margin-right 0px; var on
  // :root → margin-right = panel width.
  //
  // useLayoutEffect (not useEffect): the panel renders immediately, but
  // offset-consuming surfaces with no transition — notably the fixed voice
  // layout (.voice-content-wrapper, 769–1023px) — would otherwise paint one
  // frame at right:0 and then jump once a post-paint effect set the var.
  // useLayoutEffect writes the var before the browser paints, matching the old
  // inline-style's synchronous-with-render behaviour (Codex review). Client-only
  // SPA, so no SSR useLayoutEffect warning applies.
  React.useLayoutEffect(() => {
    const width = isOpen ? `${panelWidth}px` : '0px';
    document.documentElement.style.setProperty('--help-panel-width', width);
    return () => document.documentElement.style.setProperty('--help-panel-width', '0px');
  }, [isOpen, panelWidth]);

  return (
    <div className="App">
      <AppRoutes />
      <GuestChatPrefetch />
      <HelpPanelHost />
      <FloatingHelpButton />
    </div>
  );
}

function TourMount({ children }) {
  // Bridges OnboardingContext + BrandingContext into the TourProvider.
  // Mid-session kill-switch flips (onboardingV2Enabled true → false) just
  // re-render with `enabled={false}` and TourProvider unmounts cleanly.
  const branding = useBranding();
  const onboarding = useOnboarding();
  return (
    <TourProvider
      enabled={branding.onboardingV2Enabled === true}
      onboardingStatus={onboarding?.status}
      postOnboardingState={onboarding?.postState || (() => Promise.resolve())}
      onLocalStateChange={onboarding?.applyLocalUpdate}
      onStartWelcomeScenario={onboarding?.startWelcomeScenario}
    >
      {children}
    </TourProvider>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <AuthProvider>
        <ThemeProvider>
          <BrandingProvider>
            <Router>
              <CanonicalTag />
              <OnboardingProvider>
                <HelpPanelProvider>
                  <ToastProvider>
                    {/* #1696 (2026-07-27 DECIDED): one fan-open FabMenu (home / help /
                        report-a-bug / Ask Praxis) plus the debrief coach FAB kept in
                        its own corner, prominent, outside the menu. FabStackProvider
                        wraps everything below so the debrief coach FAB — mounted deep
                        inside AppContent's route tree, not as a sibling here — shares
                        the same registry as the FABs that ARE siblings. BugReporter /
                        HomeButton / AnalyticsAskFab / the App-level help button now
                        register into the 'menu' corner and render no fixed-position
                        markup of their own; <FabMenu/> is what actually renders the
                        trigger + fan-open panel. */}
                    <FabStackProvider>
                      <TourMount>
                        <AppContent />
                      </TourMount>
                      <TestingPanelHost />
                      <BugReporter />
                      <HomeButton />
                      <AnalyticsAskFab />
                      <FabMenu />
                    </FabStackProvider>
                  </ToastProvider>
                </HelpPanelProvider>
              </OnboardingProvider>
            </Router>
          </BrandingProvider>
        </ThemeProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  )
}

export default App